import React from "react";
import { Subscription } from "rxjs";
import NavigationTokens from "../../constants/navigationTokens";
import { IDisplayState } from "../../interfaces/IDisplayState";
import MenuItemService from "../../utilities/menuItemService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import UserService from "../../utilities/userService";
import NeedHelp from "../needHelp/needHelp";
import MenuIds from "./MenuIds";
import MenuItem from "./menuItem";
import MenuItemDivider from "./menuItemDivider";
import ConfigProps from "../../constants/configProps";
import ConfigPropService from "../../utilities/configPropService";
import LogoutUrlService from "../../utilities/logoutUrlService";
import EnvironmentService from "../../utilities/environmentService";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

export interface IMainMenuProps {
  myListsUrl?: string;
  pushMenuStack: (
    id: string | undefined,
    label: string | undefined,
    icon: string | undefined,
    svg: (() => React.ReactNode) | undefined,
    src: string | undefined,
    href: string | undefined,
    subMenuUseHref: boolean | undefined,
    linkClass: string | undefined,
    extras: unknown | undefined,
  ) => void;
  displayState: IDisplayState;
  isLoggedIn: boolean;
}

export interface IMainMenuState {
  ordersHref: string;
  dashboardHref: string;
  myWbRecentItemsHref: string;
  myWbRecentlyViewedHref: string;
  invoicesAndPaymentsHref: string;
  logoutHref: string;
  myAccountAndSettingsHref: string;
  displaySignOut: boolean;
  displayAccountCenterDashboard: boolean;
  contactUsHref: string;
  shopByItemIdHref: string;
  displayRecentlyViewed: boolean;
  displayEasyReorder: boolean;
  displayQuickOrderLinks: boolean;
  displayAccountAndSettingsLink: boolean;
  shopUrl: string;
}

class MainMenu extends React.Component<IMainMenuProps, IMainMenuState> {
  subsToUnsubscribe: Subscription[] = [];

  constructor(props: IMainMenuProps) {
    super(props);

    this.state = {
      ordersHref: "",
      dashboardHref: "",
      myWbRecentItemsHref: "",
      myWbRecentlyViewedHref: "",
      invoicesAndPaymentsHref: "",
      logoutHref: "",
      myAccountAndSettingsHref: "",
      displaySignOut: false,
      displayAccountCenterDashboard: false,
      contactUsHref: "",
      shopByItemIdHref: WBMUrlService.getQuickOrderUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomQuickOrder!,
      ),
      displayRecentlyViewed: false,
      displayEasyReorder: false,
      displayQuickOrderLinks: false,
      displayAccountAndSettingsLink: false,
      shopUrl: WBMUrlService.getShopUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomSearch!,
      ),
    };
  }

  componentDidMount() {
    this.subsToUnsubscribe.push(
      NavigationTokensService.get([
        NavigationTokens.orderHistory,
        NavigationTokens.myWbRecentItems,
        NavigationTokens.accountStatement,
        NavigationTokens.contactUs,
        NavigationTokens.OISignup,
        NavigationTokens.myAccount,
      ]).subscribe((response) => {
        this.setState({
          ordersHref: response[NavigationTokens.orderHistory],
          contactUsHref: response[NavigationTokens.contactUs],
        });

        let myWbRecentItemsHrefEcom = response[NavigationTokens.myWbRecentItems]
          .toLowerCase()
          .replace(
            `${NavigationTokens.myWbRecentItems.toLowerCase()}.aspx`,
            "mobileapp/#/myWBRecentItems/PricePlan=UserOrdered&fi=1&fr=1&sc=OF",
          );

        let myWbRecentlyViewedHrefEcom = response[
          NavigationTokens.myWbRecentItems
        ]
          .toLowerCase()
          .replace(
            `${NavigationTokens.myWbRecentItems.toLowerCase()}.aspx`,
            "mobileapp/#/myWBRecentItems/PricePlan=UserViewed&fi=1&fr=1&sc=OF",
          );

        this.subsToUnsubscribe.push(
          MenuItemService.onlineInvoicingSignUpUrl().subscribe((value) => {
            this.setState({
              invoicesAndPaymentsHref: value
                ? response[NavigationTokens.OISignup]
                : response[NavigationTokens.accountStatement],
            });
          }),
        );

        this.subsToUnsubscribe.push(
          ConfigPropService.get([
            ConfigProps.EnableResponsiveSearchResults,
            ConfigProps.EnableResponsiveAccountCenter,
            ConfigProps.EnableResponsiveAccountDashboard,
          ]).subscribe((configProps: Record<string, string>) => {
            this.setState({
              myWbRecentItemsHref: ConfigPropService.isYesOrTrue(
                configProps[ConfigProps.EnableResponsiveSearchResults],
              )
                ? `${this.state.shopUrl}?fi=1&fr=1&oa=1&priceplan=userordered&sc=or`
                : myWbRecentItemsHrefEcom,
              myWbRecentlyViewedHref: ConfigPropService.isYesOrTrue(
                configProps[ConfigProps.EnableResponsiveSearchResults],
              )
                ? `${this.state.shopUrl}?priceplan=userviewed&rvi=1`
                : myWbRecentlyViewedHrefEcom,
            });

            const enableResponsiveAccountCenter = ConfigPropService.isYesOrTrue(
              configProps[ConfigProps.EnableResponsiveAccountCenter],
            );

            const enableResponsiveAccountDashboard =
              ConfigPropService.isYesOrTrue(
                configProps[ConfigProps.EnableResponsiveAccountDashboard],
              );

            const responsiveAccountCenterUrl =
              WBMUrlService.getAccountCenterUrl(
                BaseHostUrlService.get(),
                EnvironmentService.get().ecom,
                true,
              );

              const accountDashboardUrl =
              WBMUrlService.getAccountDashboardUrl(
                BaseHostUrlService.get(),
                `${
                  enableResponsiveAccountDashboard
                    ? EnvironmentService.get().ecomVnextUrl
                    : EnvironmentService.get().ecom
                }`,
                enableResponsiveAccountDashboard,
              );

            this.setState({
              myAccountAndSettingsHref: enableResponsiveAccountCenter
                ? responsiveAccountCenterUrl
                : response[NavigationTokens.myAccount],
              dashboardHref: accountDashboardUrl,
            });
          }),
        );
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displaySignOut().subscribe((value) => {
        this.setState({
          displaySignOut: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayAccountCenterDashboard().subscribe((value) => {
        this.setState({
          displayAccountCenterDashboard: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayRecentlyViewed().subscribe((value) => {
        this.setState({
          displayRecentlyViewed: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayQuickOrderLinks().subscribe((value) => {
        this.setState({
          displayQuickOrderLinks: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayAccountAndSettingsLink().subscribe((value) => {
        this.setState({
          displayAccountAndSettingsLink: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayEasyReorder().subscribe((value) => {
        this.setState({
          displayEasyReorder: value,
        });
      }),
    );

    this.subsToUnsubscribe.push(
      LogoutUrlService.get().subscribe((value) => {
        this.setState({
          logoutHref: value,
        });
      }),
    );
  }

  componentWillUnmount() {
    for (const sub of this.subsToUnsubscribe) {
      sub.unsubscribe();
    }
  }

  render() {
    const myListsProperties: Record<string, unknown> = {};

    if (this.props.myListsUrl) {
      myListsProperties.href = this.props.myListsUrl;
    } else {
      myListsProperties.pushMenuStack = this.props.pushMenuStack;
    }

    return (
      <div className="menu-items">
        {UserService.displayUserInfo() &&
          this.props.displayState.displayDashboard && (
            <MenuItem
              icon="dashboard"
              href={this.state.dashboardHref}
              label="Dashboard"
            />
          )}

        {UserService.displayUserInfo() && <MenuItemDivider />}

        {UserService.displayUserInfo() &&
          this.state.displayAccountAndSettingsLink && (
            <MenuItem
              icon={"account_circle"}
              href={this.state.myAccountAndSettingsHref}
              label="My Account & Settings"
            />
          )}

        {UserService.displayUserInfo() &&
          this.state.invoicesAndPaymentsHref && (
            <MenuItem
              icon="attach_money"
              href={this.state.invoicesAndPaymentsHref}
              label="Invoices & Payments"
            />
          )}

        {UserService.displayUserInfo() &&
          this.props.displayState.displayOrders && (
            <MenuItem
              svg={() => {
                return (
                  <img
                    src="https://images.wbmason.com/SVG/orders-24.svg"
                    alt="Orders"
                  />
                );
              }}
              href={this.state.ordersHref}
              label="Orders"
            />
          )}
        {UserService.displayUserInfo() && (
          <MenuItem
            icon="favorite_border"
            label="Lists"
            menuId={MenuIds.MY_LISTS_MENU}
            {...myListsProperties}
          />
        )}

        {UserService.displayUserInfo() && <MenuItemDivider />}

        {UserService.displayUserInfo() && this.props.displayState.subTabSet && (
          <MenuItem
            icon="local_mall"
            label="Shop By Category"
            menuId={MenuIds.SHOP_BY_CATEGORY}
            pushMenuStack={this.props.pushMenuStack}
          />
        )}

        {UserService.displayUserInfo() && this.state.displayQuickOrderLinks && (
          <MenuItem
            icon="menu_book"
            href={this.state.shopByItemIdHref}
            label="Shop by Item Number"
          />
        )}

        {UserService.displayUserInfo() && this.state.displayEasyReorder && (
          <MenuItem
            icon="replay"
            href={this.state.myWbRecentItemsHref}
            label="Order Again"
          />
        )}

        {UserService.displayUserInfo() && this.state.displayRecentlyViewed && (
          <MenuItem
            icon="history"
            label="Recently Viewed"
            href={this.state.myWbRecentlyViewedHref}
          />
        )}

        {UserService.displayUserInfo() && <MenuItemDivider />}

        {UserService.displayUserInfo() && (
          <MenuItem
            label="Customer Support"
            icon="contact_support"
            href={this.state.contactUsHref}
          />
        )}

        {this.props.isLoggedIn && this.state.displaySignOut && (
          <a href={this.state.logoutHref}>
            <button className="sign-out-button">
              <MenuItem icon="exit_to_app" label="Sign Out" />
            </button>
          </a>
        )}

        {(UserService.displayUserInfo() ||
          (this.props.isLoggedIn && this.state.displaySignOut)) && (
          <MenuItemDivider />
        )}

        <NeedHelp />
      </div>
    );
  }
}

export default MainMenu;
